/**
 * `Footer` renders the site footer for dealer, renewals, and (some) customer
 * journeys.
 *
 * Behaviour:
 * - Renders contact details, legal documents, and trading name sections from
 *   CMS content.
 * - Applies a journey-specific CSS class (dealer vs renewals).
 * - Adds extra UK-only links (privacy policy and vulnerability disclosure).
 * - Supports inline legal documents by opening `FooterLegalDocument` modal when
 *   a CMS item provides HTML content.
 * - Hidden for call-centre journeys and for customer journeys with an active
 *   instance id.
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import { TitledListItem } from '../common/CommonTypes';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { Link, generatePath } from 'react-router-dom';
import FooterLegalDocument from './FooterLegalDocument';
import { CountryIsoCodes } from '../common/Constants';
import { useAppSelector } from '../../app-state/Hooks';
import { useTranslation } from 'react-i18next';

const Footer = React.memo(function Footer(): JSX.Element {
  const user = useAppSelector((s) => s.user);
  const appConfig = useAppSelector((s) => s.appConfig);
  const siteContent = useAppSelector((s) => s.content);
  const { t } = useTranslation();

  const isUkSite = appConfig.countryCode === CountryIsoCodes.GB;

  const { instanceId } = user;
  const [showLegalDocDialog, setShowLegalDocDialog] = useState(false);

  const [legalDocDialogTitle, setLegalDocDialogTitle] = useState('');
  const [legalDocDialogContent, setLegalDocDialogContent] = useState('');

  const getFooterClass = () => {
    if (appConfig.journeyType === Journey.Renewals)
      return 'renewal-journey-footer';
    else if (appConfig.journeyType === Journey.DealerJourney)
      return 'dealer-journey-footer';
  };

  const contactDetailsTitle = siteContent?.footer?.contactDetailItems.title;
  const contactDetailItems = siteContent?.footer?.contactDetailItems.items;

  const legalDocsTitle = siteContent?.footer?.legalDocumentsItems.title;
  const legalDocsItems = siteContent?.footer?.legalDocumentsItems.items;

  const tradingNameTitle = siteContent?.footer?.tradingNameItems.title;
  const tradingNameItems = siteContent?.footer?.tradingNameItems.items;

  const showWithdrawalLink = siteContent?.withdrawalPage?.showWithdrawalLink;

  const renderContactDetails = () => {
    if (contactDetailItems) {
      const items = contactDetailItems.map((item, index) =>
        renderItems(item, index)
      );

      return <ul className='footer-contact-list'>{items}</ul>;
    } else {
      return <></>;
    }
  };

  const renderItems = (item: TitledListItem, index: number) => {
    if (item.text.includes('@')) {
      return (
        <li className='cms' key={index}>
          <FontAwesomeIcon icon={faEnvelope} className='m05-right' />
          <a href={'mailto:' + item.text}>{item.text}</a>
        </li>
      );
    } else {
      return (
        <li className='cms' key={index}>
          <FontAwesomeIcon icon={faPhone} className='m05-right' />
          <a href={'tel:' + item.text}>{item.text}</a>
        </li>
      );
    }
  };

  const renderTradingNameItems = () => {
    if (tradingNameItems) {
      const length = tradingNameItems.length - 1;

      const items = tradingNameItems.map((item, index) => (
        <p key={index} className={getClassName(length, index)}>
          {item.text}
        </p>
      ));

      return <>{items}</>;
    } else {
      return <></>;
    }
  };

  const renderDoc = (item: TitledListItem, index: number) => {
    if (item?.file || item?.externalLink) {
      return (
        <a
          key={`legal-doc-${index}`}
          href={item.file ?? item.externalLink}
          target='_blank'
          rel='noopener noreferrer'>
          {item.text}
        </a>
      );
    } else if (item?.content) {
      return (
        <Link
          id={`legal-doc-${index}`}
          to='#'
          onClick={() => onClickLegalDoc(item)}>
          {item.text}
        </Link>
      );
    } else {
      return <></>;
    }
  };

  const renderDocs = () => {
    if (legalDocsItems) {
      return legalDocsItems.map((item, index) => (
        <li key={item.text}>{renderDoc(item, index)}</li>
      ));
    } else {
      return <></>;
    }
  };

  const renderLegalDocuments = () => {
    return (
      <>
        <h2 className='m05-bottom bold section-header m3-top'>
          {legalDocsTitle}
        </h2>
        <ul className='footer-legal-list'>
          {isUkSite && (
            <li>
              <a
                href='https://www.view-privacy-policy.co.uk/Privacy-Policy'
                target='_blank'
                rel='noopener noreferrer'>
                Privacy policy
              </a>
            </li>
          )}
          {renderDocs()}
          {isUkSite && (
            <li>
              <a
                href='https://amtrustfinancial.com/vulnerability-disclosure'
                target='_blank'
                rel='noopener noreferrer'>
                Vulnerability Disclosure Policy
              </a>
            </li>
          )}
          {showWithdrawalLink && (
            <li>
              <Link to={generatePath(PageAbsolutePath.Withdrawal)}>
                {t('footer.right-of-withdrawal')}
              </Link>
            </li>
          )}
        </ul>
      </>
    );
  };

  const onCloseLegalDocModal = () => {
    setShowLegalDocDialog(false);
  };

  const onClickLegalDoc = (item: TitledListItem) => {
    setShowLegalDocDialog(true);

    setLegalDocDialogTitle(item.text);
    setLegalDocDialogContent(item?.content ?? '');
  };

  const getClassName = (length: number, index: number) => {
    let className = '';

    if (index === length) {
      className = 'copyright m05-top';
    }

    return className;
  };

  if (
    appConfig.journeyType === Journey.DealerJourney ||
    appConfig.journeyType === Journey.Renewals ||
    appConfig.journeyType === Journey.General ||
    (appConfig.journeyType === Journey.Customer &&
      CommonValidation.isEmpty(instanceId))
  ) {
    return (
      <footer className={getFooterClass()}>
        <div className='flex-row footer-container footer-font'>
          <div className='m05-bottom'>
            <h2 className='m05-bottom bold section-header m3-top'>
              {contactDetailsTitle}
            </h2>
            {renderContactDetails()}
          </div>
          <div className='m05-bottom'>{renderLegalDocuments()}</div>
          <div className='m05-bottom'>
            <h2 className='bold m05-bottom section-header m3-top cms'>
              {tradingNameTitle}
            </h2>
            {renderTradingNameItems()}
          </div>
        </div>
        <FooterLegalDocument
          showDialog={showLegalDocDialog}
          title={legalDocDialogTitle}
          content={legalDocDialogContent}
          onCloseModal={onCloseLegalDocModal}
        />
      </footer>
    );
  }

  return <></>;
});

export default Footer;
